import { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import clsx from 'clsx';
import { type AnnouncementColorOption } from '@/app/pages/notifications/announcements/core/_models';
import {
  addAnnouncementCustomColor,
  loadAnnouncementCustomColors,
  saveAnnouncementCustomColors,
} from '@/app/pages/notifications/announcements/core/_utils';

type AnnouncementColorMenuProps = {
  id: string;
  label: string;
  icon: string;
  colors: AnnouncementColorOption[];
  customColorsKey: string;
  activeColor?: string;
  disabled: boolean;
  onSelect: (color: string) => void;
  onClear: () => void;
};

/** Cor do texto e marca-texto: a paleta, o "+" para outra cor e as últimas cores escolhidas. */
function AnnouncementColorMenu({
  id,
  label,
  icon,
  colors,
  customColorsKey,
  activeColor,
  disabled,
  onSelect,
  onClear,
}: AnnouncementColorMenuProps) {
  const [show, setShow] = useState(false);
  const [customColors, setCustomColors] = useState(() => loadAnnouncementCustomColors(customColorsKey));
  const pickerRef = useRef<HTMLInputElement>(null);

  const selectColor = (color: string) => {
    onSelect(color);
    setShow(false);
  };

  const clearColor = () => {
    onClear();
    setShow(false);
  };

  const addCustomColor = (color: string) => {
    const nextColors = addAnnouncementCustomColor(customColors, color);
    setCustomColors(nextColors);
    saveAnnouncementCustomColors(customColorsKey, nextColors);
    const normalizedColor = color.toLowerCase();
    selectColor(normalizedColor);
  };
  const addCustomColorRef = useRef(addCustomColor);
  addCustomColorRef.current = addCustomColor;

  // O `change` nativo só dispara quando a pessoa fecha o seletor; o onChange do React dispara a cada arrasto.
  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    const handlePickedColor = () => addCustomColorRef.current(picker.value);
    picker.addEventListener('change', handlePickedColor);
    return () => picker.removeEventListener('change', handlePickedColor);
  }, [show]);

  const renderSwatch = (color: AnnouncementColorOption) => {
    const isActiveColor = activeColor?.toLowerCase() === color.value.toLowerCase();
    return (
      <button
        key={color.value}
        aria-label={color.label}
        aria-pressed={isActiveColor}
        className={clsx('announcement-color-swatch p-0 rounded-circle', isActiveColor && 'active')}
        style={{ backgroundColor: color.value }}
        title={color.label}
        type="button"
        onClick={() => selectColor(color.value)}
      />
    );
  };

  const recentColors = customColors.map((color): AnnouncementColorOption => ({ label: color, value: color }));

  // Clicar no seletor nativo conta como clique dentro do menu: só o clique fora fecha.
  return (
    <Dropdown autoClose="outside" show={show} onToggle={setShow}>
      <Dropdown.Toggle
        aria-label={label}
        className="btn btn-sm btn-icon btn-active-light-primary"
        disabled={disabled}
        id={id}
        title={label}
        variant=""
      >
        <i className={clsx('bi fs-5', icon)} style={{ color: activeColor }} />
      </Dropdown.Toggle>
      <Dropdown.Menu className="p-3">
        <div className="d-flex gap-2 mb-2">
          {colors.map(renderSwatch)}
          <label
            className="announcement-color-swatch announcement-color-add d-inline-flex flex-center position-relative m-0 p-0 rounded-circle border-dashed border-gray-500 cursor-pointer"
            title="Escolher outra cor"
          >
            <i aria-hidden="true" className="bi bi-plus" />
            <input
              ref={pickerRef}
              aria-label="Escolher outra cor"
              className="position-absolute top-0 end-0 bottom-0 start-0 w-100 h-100 p-0 border-0 opacity-0 cursor-pointer"
              defaultValue="#000000"
              type="color"
            />
          </label>
        </div>
        {recentColors.length > 0 ? <div className="d-flex gap-2 mb-2">{recentColors.map(renderSwatch)}</div> : null}
        <button className="btn btn-sm btn-light w-100" type="button" onClick={clearColor}>
          Sem cor
        </button>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default AnnouncementColorMenu;
